/* eslint-disable react-hooks/exhaustive-deps */
import React, { Suspense, useEffect, useRef } from "react";
import { Route, Routes, Navigate, useLocation } from "react-router-dom";
import { Modal, Spin } from "antd";
// import LandingPage from "./pages/Landing/index.page";
import { setAuthData } from "./redux/user/user.actions";
import triggerSleepClient from "./services/timeout-services";
import GlobalLogoutClient from "./services/forced-logout-services";
import "./utils/axios-interceptor";
import ToastComponent from "./components/Toast/toast";
import { axiosClient } from "./utils/axios-config";
import { dataConstants } from "./constants/data-constants";
// import NotFound from "./pages/404/index.page";
// import ChangePassword from "./pages/Password/index.page";
// import SignIn from "./pages/SignIn/index.page";
// import Offline from "./pages/Offline/index.page";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import ResolutionTracker from "./pages/Resolution/index.page";
import Registration from "./pages/Registration/index.page";
import { startSocketConnection } from "./redux/auctions/auctions.actions";
import CacheBuster from "./components/CacheBuster/CacheBuster";
import NewRegistration from "./pages/NewRegistration/index.page";
import ErrorBoundary from "./pages/ErrorBoundary/index.page";
import triggerAnalyticsClient from "./services/analytics-services";
import TermsandConditions from "./pages/TermsConditions/index.page";
import PrivacyPolicy from "./pages/PrivacyPolicy/index.page";
import { isMessaging } from "./firebase";
import { onMessage } from "firebase/messaging";
import InstallAppBanner from "./components/Banner/InstallBanner";
import useTabGuard from "./utils/useTabDispatch";

const SignIn = React.lazy(() => import("./pages/SignIn/index.page"));
const LandingPage = React.lazy(() => import("./pages/Landing/index.page"));
const Offline = React.lazy(() => import("./pages/Offline/index.page"));
const ChangePassword = React.lazy(() => import("./pages/Password/index.page"));
const NotFound = React.lazy(() => import("./pages/404/index.page"));

const App: React.FC = () => {
  const isLogged = useSelector((state: any) => state.user.isLogged);
  const authData = useSelector((state: any) => state.user.authData);
  const dispatch = useDispatch();
  let timeout = useRef<NodeJS.Timeout>();
  let timer = useRef<NodeJS.Timeout>();
  let refreshCallTimer = useRef<NodeJS.Timeout>();
  let refreshErrorSignoutTimer = useRef<NodeJS.Timeout>();
  let instance = useRef<any>();
  const [modal, contextHolder] = Modal.useModal();
  let minutesToGo: number;

  !isLogged && triggerAnalyticsClient.setInitAmplitude();

  useTabGuard();

  /**
   * @function
   * @description - Calculates timings based on expiry_times of tokens and call refresh tokens api
   **/
  const refreshTimer = () => {
    clearTimeout(timeout.current as NodeJS.Timeout);
    const callingTime =
      authData.access_token_expiry * 1000 -
      (authData.access_token_expiry * 1000) / 60;
    timeout.current = setTimeout(() => {
      if (authData.refresh_token) {
        getRefreshFn();
      } else {
        clearTimeout(timeout.current as NodeJS.Timeout);
      }
    }, callingTime);
  };
  useEffect(() => {
    let unsubscribe;
    isMessaging()
      .then((messagingData) => {
        if (messagingData) {
          unsubscribe = onMessage(messagingData, (payload) => {
            console.log("Message received. ", payload);
            // Customize your in-app notification here
            // alert(JSON.stringify(payload));
          });
        }
      })
      .catch((e) => console.log("subscribe error", e));

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);
  /**
   * @function
   * @description Triggers refresh token API call
   **/
  const getRefreshFn = async () => {
    try {
      const refreshResponse = await axiosClient.post(
        dataConstants.refreshTokenUrl,
        {
          access_token: authData.access_token,
          refresh_token: authData.refresh_token,
        }
      );
      if (refreshResponse.status === 200) {
        dispatch(
          setAuthData({
            ...authData,
            access_token: refreshResponse.data.data.access_token,
            refresh_token: refreshResponse.data.data.refresh_token,
            access_token_expiry: refreshResponse.data.data.access_token_expiry,
            refresh_token_expiry:
              refreshResponse.data.data.refresh_token_expiry,
          })
        );
        clearInterval(timer.current as NodeJS.Timeout);
        clearInterval(refreshCallTimer.current as NodeJS.Timeout);
        clearTimeout(refreshErrorSignoutTimer.current as NodeJS.Timeout);
        if (instance.current) {
          instance.current.destroy();
          instance.current = null;
        }
        !triggerSleepClient.idleLogoutTimer &&
          !triggerSleepClient.idleAlertModalTimer &&
          !triggerSleepClient.titleInterval &&
          triggerSleepClient.onInit();
        await dispatch(startSocketConnection());
      }
    } catch (error: any) {
      if (!instance.current) {
        if (error.response.data.msg == "Token has been revoked") {
          GlobalLogoutClient.Logout();
          return false;
        }
        const intervalTimer = (authData.access_token_expiry * 1000) / 60;
        minutesToGo = minutesToGo
          ? minutesToGo
          : authData.refresh_token_expiry -
            (authData.refresh_token_expiry - authData.refresh_token_expiry / 3);
        instance.current = modal.error({
          title: "Connection Issue",
          content:
            "There is a problem connecting to the server. Please wait for some time until connection is restored otherwise application will logout automatically.",
          closable: false,
          okButtonProps: {
            style: { border: 0 },
            type: "primary",
          },
          zIndex: 1200,
          keyboard: false,
          mask: true,
          centered: true,
          okText: "Logout",
          onOk: () => GlobalLogoutClient.Logout(),
        });

        refreshCallTimer.current = setInterval(() => {
          (async () => await getRefreshFn())();
        }, intervalTimer);

        GlobalLogoutClient.Initializer(
          timeout,
          timer,
          refreshCallTimer,
          instance
        );

        refreshErrorSignoutTimer.current = setTimeout(() => {
          document.title = "Connection Timedout !!!";
          GlobalLogoutClient.Logout();
        }, minutesToGo * 55000);
      }
    }
  };
  const checkSession = async () => {
    try {
      const checkResponse = await axiosClient.post(
        dataConstants.checkSessionUrl,
        {},
      );
      if (checkResponse.status === 200) {
        console.log(checkResponse.data);
      }
    } catch (error: any) {
      if (error.response.data?.msg == "Token has been revoked") {
        GlobalLogoutClient.Logout();
      }
    }
  };
  useEffect(() => {
    clearTimeout(timeout.current as NodeJS.Timeout);
    if (isLogged) {
      triggerSleepClient.onTouchStart();
      (async () => {
        await getRefreshFn();
        refreshTimer();
        GlobalLogoutClient.Initializer(
          timeout,
          timer,
          refreshCallTimer,
          instance
        );
      })();
    }
  }, []);
  useEffect(() => {
    let refreshIntervalID;
    if (isLogged) {
      refreshIntervalID = setInterval(() => {
        checkSession();
      }, 5000);
    }
    return () => {
      clearInterval(refreshIntervalID);
    };
  }, [isLogged]);

  useEffect(() => {
    if (authData.refresh_token) {
      refreshTimer();
      GlobalLogoutClient.Initializer(
        timeout,
        timer,
        refreshCallTimer,
        instance
      );
      minutesToGo =
        (authData.refresh_token_expiry -
          (authData.refresh_token_expiry - authData.refresh_token_expiry / 3)) /
        60;
    }
    return () => clearTimeout(timeout.current as NodeJS.Timeout);
  }, [authData.access_token]);

  return (
    <CacheBuster>
      {({ loading, isLatestVersion, refreshCacheAndReload }: any) => {
        if (loading) return null;
        if (!loading && !isLatestVersion) {
          refreshCacheAndReload();
        }
        return (
          <ModalWrapper
            instance={instance}
            getRefreshFn={getRefreshFn}
            contextHolder={contextHolder}
          />
        );
      }}
    </CacheBuster>
  );
};

const RouteTracker = () => {
  const location = useLocation();
  useEffect(() => {
    // Track the route change with Amplitude
    triggerAnalyticsClient.emitEvent(
      "tn_screen_navigation",
      {
        module: "Tradenext",
        activity: "Route",
      },
      {
        Path: location.pathname,
      }
    );
  }, [location]);

  return null;
};

/**
 * @function
 * @description Wraps the whole component with two different modals
 * @params - Props containing datas from redux states and initialized app states only
 **/
const ModalWrapper = (props: any) => {
  const { instance, getRefreshFn, contextHolder } = props;
  const idleModal = useSelector((state: any) => state.user.idleModal);
  return (
    <>
      {contextHolder}
      <Modal
        title="Session Alert"
        open={idleModal}
        closable={false}
        styles={{ body: { backgroundColor: "#ffffff" } }}
        maskClosable={false}
        okText="Continue"
        cancelText="Log Out"
        style={{ textAlign: "center" }}
        centered
        okButtonProps={{
          style: { backgroundColor: "#20346a", borderRadius: "16px" },
        }}
        cancelButtonProps={{
          style: { borderRadius: "16px" },
          danger: true,
          type: "primary",
        }}
        onOk={() => {
          (async () => {
            await triggerSleepClient.onTouchStart();
            getRefreshFn();
          })();
        }}
        onCancel={() => triggerSleepClient.consentLogoutUser()}
      >
        <div style={{ color: "black" }}>
          Your session is about to expire. Do you want to continue session?
        </div>
      </Modal>
      <SessionWrapper instance={instance} />
    </>
  );
};

/**
 * @function
 * @description Wraps the whole component with session browser events
 * @params - Props containing datas from redux states and initialized app states only
 **/
const SessionWrapper = (props: any) => {
  const { instance } = props;
  const isLogged = useSelector((state: any) => state.user.isLogged);
  const idleModal = useSelector((state: any) => state.user.idleModal);
  return (
    <div
      onMouseMove={() => {
        isLogged &&
          !instance.current &&
          !idleModal &&
          triggerSleepClient.onTouchStart();
      }}
    >
      <AppRoute />
    </div>
  );
};

/**
 * @function
 * @description Returns all the route paths along with private route paths also
 * @params - Props containing datas from redux states only
 **/
const AppRoute = () => {
  const isLogged = useSelector((state: any) => state.user.isLogged);
  const userData = useSelector((state: any) => state.user.userData);
  return (
    <ErrorBoundary>
      <Suspense
        fallback={
          <div
            style={{
              marginTop: "30em",
              textAlign: "center",
            }}
          >
            <Spin size="large" />
          </div>
        }
      >
        <RouteTracker />
        <Routes>
          <Route element={<Offline />}>
            <Route element={<ResolutionTracker />}>
              <Route element={<ToastComponent />}>
                <Route
                  path="/signin"
                  element={isLogged ? <Navigate to="/home" /> : <SignIn />}
                />
                <Route path="/register" element={<Registration />} />
                <Route path="/newregister" element={<NewRegistration />} />
                <Route
                  path="/termsandcondition"
                  element={<TermsandConditions />}
                />
                <Route path="/privacypolicy" element={<PrivacyPolicy />} />
                <Route
                  element={
                    userData?.is_first_time_login && (
                      <ChangePassword close={false} />
                    )
                  }
                >
                  <Route
                    path="/home"
                    element={isLogged ? <LandingPage /> : <SignIn />}
                  />
                </Route>
                {/* <Route path="*" element={<NotFound />} /> */}
                <Route path="*" element={<Navigate to="/signin" />} />
              </Route>
            </Route>
            <Route path="/" element={<Navigate to="/signin" />} />
          </Route>
        </Routes>
        <InstallAppBanner />
      </Suspense>
    </ErrorBoundary>
  );
};

export default App;
